import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Box } from '@mui/material';

import { uaRestore } from '@/shared/theme/authored-content';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

interface Props {
  content: string;
  editable: boolean;
  onUpdate: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

/**
 * Lightweight inline Tiptap editor for rubric cells and descriptions.
 * Renders bare rich text with basic formatting; no toolbar chrome.
 */
const MiniTiptap: React.FC<Props> = ({
  content,
  editable,
  onUpdate,
  placeholder = '',
  minHeight = 60,
}) => {
  const suppressRef = useRef(false);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        // Keep it lightweight: no horizontal rule, code block, or blockquote
        horizontalRule: false,
        code: false,
        codeBlock: false,
        blockquote: false,
      }),
    ],
    content,
    editable,
    editorProps: {
      attributes: {
        class: 'rubric-mini-editor',
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor: instance }) => {
      if (suppressRef.current) return;
      onUpdateRef.current(instance.getHTML());
    },
  });
  const liveEditor = getLiveEditor(editor);

  // Sync editable prop
  useEffect(() => {
    if (liveEditor && liveEditor.isEditable !== editable) {
      liveEditor.setEditable(editable);
    }
  }, [liveEditor, editable]);

  // Sync content from parent (e.g. after AI update) — only when editor is not focused
  useEffect(() => {
    if (!liveEditor) return;
    if (liveEditor.isFocused) return;
    const currentHTML = liveEditor.getHTML();
    // Normalize empties
    const normalize = (html: string) => (html === '<p></p>' ? '' : html);
    if (normalize(currentHTML) === normalize(content)) return;
    suppressRef.current = true;
    liveEditor.commands.setContent(content || '', { emitUpdate: false });
    suppressRef.current = false;
  }, [content, liveEditor]);

  return (
    <Box
      sx={{
        minHeight,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '& > div': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
        '& .rubric-mini-editor': {
          outline: 'none',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          p: 1,
          minHeight,
          flex: 1,
          // Only the browser defaults this editor's enabled nodes rely on, not the
          // full authored-content contract — that would give paragraphs a bottom
          // margin the rubric grid does not have today. StarterKit here enables only
          // h3 plus lists and links; blockquote, code, codeBlock, and horizontalRule
          // are switched off, and there is no image extension.
          ...uaRestore.heading3,
          ...uaRestore.lists,
          ...uaRestore.link,
          '& p': { m: 0 },
          '&[data-placeholder]:empty::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            pointerEvents: 'none',
            fontStyle: 'italic',
            fontSize: '0.8125rem',
          },
        },
        '& .tiptap': {
          outline: 'none',
        },
        border: editable ? 1 : 0,
        borderColor: 'divider',
        borderRadius: '4px',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:focus-within': editable
          ? {
              borderColor: 'primary.main',
              // Concatenating a hex alpha onto palette.primary.main breaks once the
              // palette holds `var(--…)` references, so mix in CSS instead.
              boxShadow:
                '0 0 0 2px color-mix(in oklch, var(--mui-palette-primary-main), transparent 88%)',
            }
          : {},
      }}
    >
      <EditorContent editor={liveEditor} />
    </Box>
  );
};

export default React.memo(MiniTiptap);
